import React, { ReactNode, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

import { FormComponent, UiComponent } from 'components';
import { CheckBox } from 'components/form';
import { getUserStatus } from 'components/user/utils/status';
import { usePageOption } from 'hooks';
import useCheckTable from 'hooks/useCheckTable';
import useModal from 'hooks/useModal';
import { UserGroupQuery } from 'queries';
import { DEFAULT_ERROR_MESSAGE } from 'data/message';
import { tableToolbar, tableWrap } from 'styles/table';
import {
  IUserGroupMember,
  IUserGroupRemoveResult,
  SearchUserGroupFilterType,
} from 'types';
import { formatNameWithPhone } from '../Picker/format';
import AddMembers from './AddMembers';

type MemberRow = IUserGroupMember & { id: number };

const COLS: string[] = ['이름', '계정상태', '추가일'];

interface Props {
  groupId: number;
}

function Members({ groupId }: Props) {
  const { pageOption, handlePage } = usePageOption(20);
  const [filter, setFilter] = useState<SearchUserGroupFilterType>({});
  const { handleModal, handleConfirm } = useModal();
  const { mutateAsync: removeMembers } = UserGroupQuery.useRemoveMembers();

  const { data } = UserGroupQuery.useGetMembers(groupId, {
    page: pageOption.page,
    size: pageOption.size,
    ...filter,
  });

  const rows: MemberRow[] = useMemo(
    () =>
      (data?.data || []).map((m: IUserGroupMember) => ({
        ...m,
        id: m.userId,
        rowKey: m.userId,
      })),
    [data],
  );

  const { isAll, handleAllCheck, checkedData, handleCheck, getCheckedData } =
    useCheckTable({ data: rows });
  const checkedIds = getCheckedData();

  const handleSearch = (d: SearchUserGroupFilterType) => {
    setFilter({ name: d.name?.trim() || undefined });
    handlePage({ ...pageOption, page: 1 });
  };

  const openAdd = () =>
    handleModal('회원 추가', <AddMembers groupId={groupId} />, <></>);

  const handleRemove = () => {
    if (checkedIds.length === 0) return;
    handleConfirm({
      confirmContent: `선택한 ${checkedIds.length}명을 그룹에서 제외하시겠습니까?`,
      yesTitle: '제외',
      confirmNext: async () => {
        try {
          const res = await removeMembers({ id: groupId, userIds: checkedIds });
          const { removed } = res.data as IUserGroupRemoveResult;
          toast.success(`${removed}명 제외 완료`);
        } catch {
          toast.error(DEFAULT_ERROR_MESSAGE);
        }
      },
    });
  };

  const cols = useMemo(
    () => [
      {
        key: 'checkbox',
        render: () => <CheckBox checked={isAll} handleCheck={handleAllCheck} />,
      },
      ...COLS,
    ],
    [isAll, handleAllCheck],
  );

  const renderer: Record<
    string,
    (data: MemberRow & { row: number }) => ReactNode
  > = useMemo(
    () => ({
      checkbox: ({ id }: MemberRow) => (
        <CheckBox
          checked={checkedData[id] || false}
          handleCheck={(checked) => handleCheck(id, checked)}
        />
      ),
      이름: ({ userId, userName, userPhone }: MemberRow) => (
        <Link to={`/users/${userId}`}>
          {formatNameWithPhone(userName, userPhone)}
        </Link>
      ),
      계정상태: ({ userStatus }: MemberRow) => <>{getUserStatus(userStatus)}</>,
      추가일: ({ addedAt }: MemberRow) => (
        <>{dayjs(addedAt).format('YYYY-MM-DD')}</>
      ),
    }),
    [checkedData, handleCheck],
  );

  return (
    <UiComponent.Box>
      <div css={tableToolbar}>
        <FormComponent.Form submit={handleSearch}>
          <FormComponent.SearchInput
            name="name"
            placeholder="회원 이름으로 검색해주세요"
          />
        </FormComponent.Form>
        <div>
          <UiComponent.Button
            variant="border"
            color="error"
            disabled={checkedIds.length === 0}
            onClick={handleRemove}
          >
            {`선택 제외${checkedIds.length ? ` (${checkedIds.length})` : ''}`}
          </UiComponent.Button>
          <UiComponent.Button
            variant="contained"
            color="primary"
            onClick={openAdd}
          >
            회원 추가
          </UiComponent.Button>
        </div>
      </div>
      <div css={tableWrap}>
        <UiComponent.Table
          cols={cols}
          renderer={renderer}
          data={rows}
          sticky
          empty="그룹에 속한 회원이 없습니다"
        />
      </div>
      <UiComponent.Pagination
        pageOption={pageOption}
        handlePage={handlePage}
        totalPage={data?.totalPages || 1}
      />
    </UiComponent.Box>
  );
}

export default Members;
